/**
 * Simbología del odontograma según el formulario 033 del MSP (Historia Clínica Única de Odontología):
 * rojo = patología actual o tratamiento necesario, azul = tratamiento realizado.
 */
const S = 26, C = S / 2, I = S * 0.3, J = S * 0.7;
const R = "var(--red)", A = "var(--blue)";

type Simbolo = "caries" | "obturado" | "indicada" | "sellante" | "x" | "otra" | "endo" | "corona" | "acero" | "fija" | "removible" | "total" | "fractura" | "pulpotomia" | "dx" | "exfoliado" | "texto";

/**
 * Prótesis fija: recuadro en cada pilar y línea continua entre ellos; removible: paréntesis en cada pilar y línea
 * entrecortada (tres piezas: pilar, póntico, pilar).
 */
function Puente({ col, removible = false }: { col: string; removible?: boolean }) {
  const t = 18, g = 4, m = 3;
  const cara = (x: number) => (
    <g key={x} transform={`translate(${x} 6)`}>
      {[`0,0 ${t},0 ${t * 0.7},${t * 0.3} ${t * 0.3},${t * 0.3}`, `${t * 0.3},${t * 0.7} ${t * 0.7},${t * 0.7} ${t},${t} 0,${t}`, `0,0 ${t * 0.3},${t * 0.3} ${t * 0.3},${t * 0.7} 0,${t}`, `${t},0 ${t},${t} ${t * 0.7},${t * 0.7} ${t * 0.7},${t * 0.3}`, `${t * 0.3},${t * 0.3} ${t * 0.7},${t * 0.3} ${t * 0.7},${t * 0.7} ${t * 0.3},${t * 0.7}`]
        .map((p, k) => <polygon key={k} points={p} fill="var(--tooth)" stroke="var(--line-strong)" strokeWidth={0.8} />)}
    </g>
  );
  const xs = [4, 4 + t + g, 4 + 2 * (t + g)];
  return (
    <svg viewBox={`0 0 ${xs[2] + t + 4} 30`} width="74" height="30" aria-hidden="true">
      {xs.map(cara)}
      {removible ? (
        <g fill="none" stroke={col} strokeWidth={1.8} strokeLinecap="round">
          {[xs[0], xs[2]].map((x) => <path key={x} d={`M${x - 0.5} ${6 - m}Q${x - m - 2} ${6 + t / 2} ${x - 0.5} ${6 + t + m}M${x + t + 0.5} ${6 - m}Q${x + t + m + 2} ${6 + t / 2} ${x + t + 0.5} ${6 + t + m}`} />)}
          <path d={`M${xs[0] + t + m} ${6 + t / 2}H${xs[2] - m}`} strokeDasharray="3 2.5" strokeLinecap="butt" />
        </g>
      ) : (
        <>
          <rect x={xs[0] - m} y={6 - m} width={t + 2 * m} height={t + 2 * m} fill="none" stroke={col} strokeWidth={2} />
          <rect x={xs[2] - m} y={6 - m} width={t + 2 * m} height={t + 2 * m} fill="none" stroke={col} strokeWidth={2} />
          <path d={`M${xs[0] + t + m} ${6 + t / 2}H${xs[2] - m}`} stroke={col} strokeWidth={2} />
        </>
      )}
    </svg>
  );
}

function Pieza({ s, col, texto }: { s: Simbolo; col: string; texto?: string }) {
  const caras = [
    `0,0 ${S},0 ${J},${I} ${I},${I}`, `${I},${J} ${J},${J} ${S},${S} 0,${S}`,
    `0,0 ${I},${I} ${I},${J} 0,${S}`, `${S},0 ${S},${S} ${J},${J} ${J},${I}`, `${I},${I} ${J},${I} ${J},${J} ${I},${J}`,
  ];
  const relleno = (k: number) => (s === "caries" && k === 4 ? R : s === "obturado" && k === 4 ? A : s === "dx" && k === 4 ? "var(--red-soft)" : "var(--tooth)");
  const borde = (k: number) => (s === "indicada" && k === 4 ? R : s === "dx" && k === 4 ? R : "var(--line-strong)");
  return (
    <svg viewBox={`-6 -6 ${S + 12} ${S + 12}`} width="38" height="38" aria-hidden="true">
      {caras.map((p, k) => <polygon key={k} points={p} fill={relleno(k)} stroke={borde(k)} strokeWidth={s === "indicada" && k === 4 ? 2 : 1} strokeDasharray={s === "exfoliado" ? "2 2" : undefined} fillOpacity={s === "exfoliado" ? 0 : 1} />)}
      {s === "sellante" && <path d={`M${C} ${C - 7}V${C + 7}M${C - 6} ${C - 3.5}L${C + 6} ${C + 3.5}M${C - 6} ${C + 3.5}L${C + 6} ${C - 3.5}`} stroke={col} strokeWidth={2.2} strokeLinecap="round" />}
      {s === "x" && <path d={`M-2 -2L${S + 2} ${S + 2}M${S + 2} -2L-2 ${S + 2}`} stroke={col} strokeWidth={2.4} strokeLinecap="round" />}
      {s === "otra" && <><circle cx={C} cy={C} r={C + 2} fill="none" stroke={col} strokeWidth={2} /><path d={`M4 4L${S - 4} ${S - 4}M${S - 4} 4L4 ${S - 4}`} stroke={col} strokeWidth={2.2} strokeLinecap="round" /></>}
      {s === "endo" && <path d={`M${C} 4L${S - 5} ${S - 5}H5Z`} fill="none" stroke={col} strokeWidth={2.2} strokeLinejoin="round" />}
      {(s === "corona" || s === "acero") && <circle cx={C} cy={C} r={C + 3} fill="none" stroke={col} strokeWidth={2} strokeDasharray={s === "acero" ? "4 2" : undefined} />}
      {s === "total" && <path d={`M-4 0H${S + 4}M-4 5H${S + 4}`} stroke={col} strokeWidth={1.8} />}
      {s === "fractura" && <path d={`M4 ${S - 3}L${S * 0.42} ${S * 0.4}L${S * 0.58} ${S * 0.65}L${S - 3} 3`} fill="none" stroke={col} strokeWidth={2.2} strokeLinejoin="round" />}
      {s === "pulpotomia" && <circle cx={C} cy={C} r={3.6} fill={col} />}
      {s === "texto" && <text x={C} y={C + 4} textAnchor="middle" fontSize="10" fontWeight="700" fill={col} fontFamily="var(--mono)">{texto}</text>}
    </svg>
  );
}

const FORMULARIO_033: { nombre: string; s: Simbolo; col: string; texto?: string; nota?: string }[] = [
  { nombre: "Sellante necesario", s: "sellante", col: R },
  { nombre: "Sellante realizado", s: "sellante", col: A },
  { nombre: "Extracción indicada", s: "x", col: R },
  { nombre: "Pérdida por caries", s: "x", col: A },
  { nombre: "Pérdida (otra causa)", s: "otra", col: A, nota: "trauma, ortodoncia, periodontal o agenesia" },
  { nombre: "Endodoncia por realizar", s: "endo", col: R },
  { nombre: "Endodoncia realizada", s: "endo", col: A },
  { nombre: "Prótesis fija necesaria", s: "fija", col: R, nota: "recuadro en cada pilar, línea de pilar a pilar" },
  { nombre: "Prótesis fija realizada", s: "fija", col: A, nota: "mínimo 3 piezas: 2 pilares y el póntico" },
  { nombre: "Prótesis removible necesaria", s: "removible", col: R, nota: "paréntesis en cada pilar, línea entrecortada de pilar a pilar" },
  { nombre: "Prótesis removible realizada", s: "removible", col: A, nota: "mínimo 3 piezas, igual que la fija" },
  { nombre: "Prótesis total necesaria", s: "total", col: R },
  { nombre: "Prótesis total realizada", s: "total", col: A, nota: "sobre toda la arcada" },
  { nombre: "Corona necesaria", s: "corona", col: R },
  { nombre: "Corona realizada", s: "corona", col: A },
  { nombre: "Obturado", s: "obturado", col: A, nota: "cara pintada de azul" },
  { nombre: "Caries", s: "caries", col: R, nota: "cara pintada de rojo" },
  { nombre: "Movilidad", s: "texto", col: R, texto: "M2", nota: "grado 1 a 4" },
  { nombre: "Recesión", s: "texto", col: R, texto: "R2", nota: "grado 1 a 4" },
];

const DE_DONNA: { nombre: string; s: Simbolo; col: string; texto?: string }[] = [
  { nombre: "Obturación indicada", s: "indicada", col: R },
  { nombre: "Diagnóstico (CIE-10)", s: "dx", col: R },
  { nombre: "Fractura", s: "fractura", col: R },
  { nombre: "Pulpotomía", s: "pulpotomia", col: A },
  { nombre: "Corona de acero", s: "acero", col: A },
  { nombre: "Exfoliado", s: "exfoliado", col: A },
];
const ABREVIATURAS = "ER en erupción · FB barniz de flúor · PT pulpotomía · INC incrustación · CV carilla · PM perno muñón · IMP implante · ME mantenedor de espacio · CA corona de acero · INCL diente incluido · SUP supernumerario · QST quiste o lesión · APX apicectomía · TRC tracción ortodóncica · FRN frenectomía · ALV alveoloplastia o torus";

export function LeyendaOdontograma() {
  return (
    <details className="leyenda-033" open>
      <summary>Simbología del odontograma <span className="muted">(formulario 033 del MSP)</span></summary>
      <p className="leyenda-colores"><span><i style={{ background: R }} /> Rojo: patología actual o tratamiento necesario</span><span><i style={{ background: A }} /> Azul: tratamiento realizado</span></p>
      <ul className="leyenda-lista" aria-label="Simbología del formulario 033">
        {FORMULARIO_033.map((x) => (
          <li key={x.nombre}>{x.s === "fija" || x.s === "removible" ? <Puente col={x.col} removible={x.s === "removible"} /> : <Pieza s={x.s} col={x.col} texto={x.texto} />}<span>{x.nombre}{x.nota && <small className="muted"> · {x.nota}</small>}</span></li>
        ))}
      </ul>
      <p className="leyenda-sub">Otros registros de Donna (no están en el 033)</p>
      <ul className="leyenda-lista" aria-label="Otros símbolos">
        {DE_DONNA.map((x) => <li key={x.nombre}><Pieza s={x.s} col={x.col} texto={x.texto} /><span>{x.nombre}</span></li>)}
      </ul>
      <p className="muted" style={{ margin: "6px 0 0", fontSize: 12 }}>Abreviaturas: {ABREVIATURAS}.</p>
    </details>
  );
}

/** El dibujo de un símbolo de la simbología del 033 por su nombre (para la simbología del formulario impreso). */
export function SimboloLeyenda({ nombre }: { nombre: string }) {
  const x = FORMULARIO_033.find((s) => s.nombre === nombre);
  if (!x) return null;
  return x.s === "fija" || x.s === "removible" ? <Puente col={x.col} removible={x.s === "removible"} /> : <Pieza s={x.s} col={x.col} texto={x.texto} />;
}
